import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DateField from "../components/DateField";
import CreditorNameAutocomplete from "../components/CreditorNameAutocomplete";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EditCase() {
    const { user } = useAuth();
    const { id } = useParams();
    const nav = useNavigate();
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/cases/${id}`).then((r) => {
            setForm({
                title: r.data.title || "",
                case_type: r.data.case_type || "lien",
                creditor_name: r.data.creditor_name || "",
                creditor_email: r.data.creditor_email || "",
                debtor_name: r.data.debtor_name || "",
                amount_owed: r.data.amount_owed ?? "",
                original_amount: r.data.original_amount ?? "",
                case_number: r.data.case_number || "",
                jurisdiction: r.data.jurisdiction || "",
                filed_date: r.data.filed_date || "",
                deadline: r.data.deadline || "",
                status: r.data.status || "new",
                client_name: r.data.client_name || "",
                client_id: r.data.client_id || "",
                settled_amount: r.data.settled_amount ?? "",
                notes: r.data.notes || "",
            });
        }).catch((e) => toast.error(formatApiError(e)));
        if (user?.role === "business") api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }, [id, user]);

    if (!form) return <p className="text-slate-500">Loading…</p>;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                creditor_email: form.creditor_email || null,
                debtor_name: form.debtor_name || null,
                amount_owed: parseFloat(form.amount_owed) || 0,
                original_amount: form.original_amount ? parseFloat(form.original_amount) : null,
                client_id: form.client_id || null,
                settled_amount: form.settled_amount === "" ? null : parseFloat(form.settled_amount),
            };
            await api.patch(`/cases/${id}`, payload);
            toast.success("Case updated");
            nav(`/cases/${id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-8" data-testid="edit-case-page">
            <Link to={`/cases/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
                <ArrowLeft size={14} /> Back to case
            </Link>

            <div>
                <p className="overline">Edit case</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">{form.title || "Untitled"}</h1>
            </div>

            <form onSubmit={submit} className="space-y-6 bg-white border border-slate-200 rounded-md p-8" data-testid="edit-case-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Label htmlFor="title">Case title *</Label>
                        <Input data-testid="case-title" id="title" required value={form.title} onChange={set("title")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label>Type *</Label>
                        <Select value={form.case_type} onValueChange={(v) => setForm({ ...form, case_type: v })}>
                            <SelectTrigger data-testid="case-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="lien">Lien</SelectItem>
                                <SelectItem value="judgment">Judgment</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="case-status" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["new","investigating","disputing","negotiating","settled","closed"].map((s) => (
                                    <SelectItem key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="creditor">Creditor / Plaintiff *</Label>
                        <CreditorNameAutocomplete
                            value={form.creditor_name}
                            onChange={(v) => setForm({ ...form, creditor_name: v })}
                            onEmailSuggest={(email) => {
                                if (!form.creditor_email && email) {
                                    setForm((prev) => ({ ...prev, creditor_email: email }));
                                    toast.success(`Auto-filled dispute email: ${email}`);
                                }
                            }}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="creditor_email">Creditor email <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                        <Input
                            data-testid="case-creditor-email"
                            id="creditor_email"
                            type="email"
                            value={form.creditor_email}
                            onChange={set("creditor_email")}
                            placeholder="collections@creditor.com"
                            className="mt-1.5 h-11"
                        />
                        <p className="text-xs text-slate-500 mt-1">Used for one-click emailing of signed letters from the Documents page.</p>
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="debtor_name">Debtor / sender name <span className="text-slate-400 text-xs font-normal">(who signs the letter)</span></Label>
                        <Input
                            data-testid="case-debtor-name"
                            id="debtor_name"
                            value={form.debtor_name}
                            onChange={set("debtor_name")}
                            placeholder="e.g. Jane Doe"
                            className="mt-1.5 h-11"
                        />
                        <p className="text-xs text-slate-500 mt-1">Appears on the return address label and in the letter&apos;s signature block.</p>
                    </div>
                    {user?.role === "business" && (
                        <>
                            <div>
                                <Label>Client</Label>
                                <Select value={form.client_id || "__none__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none__" ? "" : v })}>
                                    <SelectTrigger data-testid="case-client-select" className="mt-1.5 h-11"><SelectValue placeholder="— None —" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— None —</SelectItem>
                                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="client">Client name (manual)</Label>
                                <Input data-testid="case-client" id="client" value={form.client_name} onChange={set("client_name")} className="mt-1.5 h-11" />
                            </div>
                        </>
                    )}
                    <div>
                        <Label htmlFor="amt">Amount owed (USD) *</Label>
                        <Input data-testid="case-amount" id="amt" type="number" min="0" step="0.01" required value={form.amount_owed} onChange={set("amount_owed")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="orig">Original amount</Label>
                        <Input data-testid="case-original" id="orig" type="number" min="0" step="0.01" value={form.original_amount || ""} onChange={set("original_amount")} className="mt-1.5 h-11" />
                    </div>
                    {form.status === "settled" && (
                        <div>
                            <Label htmlFor="settled">Final settled amount (USD)</Label>
                            <Input data-testid="case-settled" id="settled" type="number" min="0" step="0.01" value={form.settled_amount} onChange={set("settled_amount")} className="mt-1.5 h-11" placeholder="What did the creditor accept?" />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="num">Case / account #</Label>
                        <Input data-testid="case-number" id="num" value={form.case_number} onChange={set("case_number")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="jur">Jurisdiction</Label>
                        <Input data-testid="case-jurisdiction" id="jur" value={form.jurisdiction} onChange={set("jurisdiction")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="filed">Filed date</Label>
                        <DateField data-testid="case-filed" id="filed" value={form.filed_date} onChange={set("filed_date")} />
                    </div>
                    <div>
                        <Label htmlFor="deadline">Response deadline</Label>
                        <DateField data-testid="case-deadline" id="deadline" value={form.deadline} onChange={set("deadline")} />
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="notes">Notes / facts</Label>
                        <Textarea data-testid="case-notes" id="notes" rows={4} value={form.notes} onChange={set("notes")} className="mt-1.5" />
                    </div>
                </div>
                <div className="flex gap-3">
                    <Button data-testid="case-save-btn" type="submit" disabled={saving} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => nav(`/cases/${id}`)} className="h-11">Cancel</Button>
                </div>
            </form>
        </div>
    );
}
